import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserState } from './Context';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

const Designation = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { user } = UserState();
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [formData, setFormData] = useState({ name: '', _id: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  const fetchDesignation = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}getAllDesignations`, {
        headers: {
          Authorization: `Bearer ${user.token}`
        }
      });

      if (response.data.success) {
        setDesignations(response.data.data);
      } else {
        setMessage({ text: 'Failed to fetch designations', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Error loading designations', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.token) {
      fetchDesignation();
    }
  }, [user?.token]);

  const handleOpenModal = (type, designation = null) => {
    setModalType(type);
    if (type === 'add') {
      setFormData({ name: '', _id: '' });
    } else {
      setFormData({ name: designation.name, _id: designation._id });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({ name: '', _id: '' });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, name: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setMessage({ text: 'Designation name is required', type: 'error' });
      return;
    }

    try {
      setLoading(true);
      let response;
      
      if (modalType === 'add') {
        response = await axios.post(
          `${BASE_URL}addDesignation`,
          { name: formData.name },
          {
            headers: {
              Authorization: `Bearer ${user.token}`
            }
          }
        );
      } else {
        response = await axios.post(
          `${BASE_URL}updateDesignation`,
          { id: formData._id, name: formData.name },
          {
            headers: {
              Authorization: `Bearer ${user.token}`
            }
          }
        );
      }

      if (response.data.success) {
        setMessage({ 
          text: modalType === 'add' ? 'Designation added successfully' : 'Designation updated successfully', 
          type: 'success' 
        });
        fetchDesignation();
        handleCloseModal();
      } else {
        setMessage({ 
          text: response.data.message || 'Operation failed', 
          type: 'error' 
        });
      }
    } catch (err) {
      setMessage({ 
        text: err.response?.data?.message || 'An error occurred', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const designation = designations.find(d => d._id === id);
    if (designation.name === "Other") {
      setMessage({ text: '"Other" designation cannot be deleted', type: 'error' });
      return;
    }

    try {
      setDeleteLoading(true);
      setActionId(id);
      
      const response = await axios.post(
        `${BASE_URL}deleteDesignation`,
        { id },
        {
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        }
      );

      if (response.data.success) {
        setMessage({ text: 'Designation deleted successfully', type: 'success' });
        fetchDesignation();
      } else {
        setMessage({ text: response.data.message || 'Delete operation failed', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: err.response?.data?.message || 'An error occurred during deletion', type: 'error' });
    } finally {
      setDeleteLoading(false);
      setActionId(null);
    }
  };

  const isRestrictedDesignation = (name) => name === "Other";

  const renderMessage = () => {
    if (!message.text) return null;
    
    const bgColor = message.type === 'success' ? 'bg-green-100 border-green-500 text-green-700' : 'bg-red-100 border-red-500 text-red-700';
    
    return (
      <div className={`${bgColor} px-4 py-3 rounded border mb-4 flex justify-between items-center`}>
        <span>{message.text}</span>
        <button 
          onClick={() => setMessage({ text: '', type: '' })}
          className="text-sm"
        >
          ×
        </button>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Designation Management</h1>
        <button
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
          onClick={() => handleOpenModal('add')}
        >
          <FaPlus className="mr-2" /> Add Designation
        </button>
      </div>

      {renderMessage()}

      {loading && !designations.length ? (
        <div className="flex justify-center items-center h-40">
          <AiOutlineLoading3Quarters className="animate-spin text-3xl text-blue-500" />
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {designations.map((designation) => (
                <tr key={designation._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{designation.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleOpenModal('update', designation)}
                      className={`text-blue-600 hover:text-blue-900 mr-4 ${isRestrictedDesignation(designation.name) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={isRestrictedDesignation(designation.name)}
                      title={isRestrictedDesignation(designation.name) ? "Cannot edit this designation" : "Edit"}
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(designation._id)}
                      className={`text-red-600 hover:text-red-900 ${isRestrictedDesignation(designation.name) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={isRestrictedDesignation(designation.name) || (deleteLoading && actionId === designation._id)}
                      title={isRestrictedDesignation(designation.name) ? "Cannot delete this designation" : "Delete"}
                    >
                      {deleteLoading && actionId === designation._id ? (
                        <AiOutlineLoading3Quarters className="animate-spin" />
                      ) : (
                        <FaTrash />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal for Add/Update Designation */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {modalType === 'add' ? 'Add New Designation' : 'Update Designation'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                  Designation Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter designation name"
                />
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    modalType === 'add' ? 'Add' : 'Update'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Designation;